import React, { useState } from "react";
import { FileText, Wrench, Receipt, FolderOpen, CalendarClock, CalendarCheck, ChevronRight, HardHat } from "lucide-react";
import { StatusPill } from "@/components/PortalShell";
import Lightbox from "@/components/Lightbox";
import ReportDetail from "@/components/ReportDetail";

const KIND = {
  report: { icon: FileText, color: "#5B6440", bg: "#e7ecdf" },
  issue: { icon: Wrench, color: "#8a5a1f", bg: "#f3e4cf" },
  invoice: { icon: Receipt, color: "#6b4e8a", bg: "#ece2f3" },
  document: { icon: FolderOpen, color: "#726A5C", bg: "#ece7df" },
  event: { icon: CalendarClock, color: "#3a5a6b", bg: "#dfe9ef" },
  appointment: { icon: CalendarCheck, color: "#3a5170", bg: "#e4e9f0" },
  job: { icon: HardHat, color: "#8a5a1f", bg: "#f0e6d8" },
};
const FILTERS = [
  { key: "all", label: "All", kinds: null },
  { key: "appointment", label: "Scheduled", kinds: ["appointment"] },
  { key: "report", label: "Visits", kinds: ["report"] },
  { key: "job", label: "Contractor", kinds: ["job"] },
  { key: "event", label: "Events", kinds: ["event"] },
  { key: "issue", label: "Issues", kinds: ["issue"] },
  { key: "invoice", label: "Invoices", kinds: ["invoice"] },
  { key: "document", label: "Documents", kinds: ["document"] },
];

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export default function Timeline({ items, canManage, onChanged }) {
  const [filter, setFilter] = useState("all");
  const [lb, setLb] = useState(null);
  const [openId, setOpenId] = useState(null);

  if (!items?.length) return <p style={{ color: "var(--muted)" }}>Nothing recorded yet.</p>;

  const present = new Set(items.map((i) => i.kind));
  const filters = FILTERS.filter((f) => !f.kinds || f.kinds.some((k) => present.has(k)));
  const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const shown = active.kinds ? items.filter((i) => active.kinds.includes(i.kind)) : items;
  const spend = items.reduce((s, i) => s + ((i.kind === "event" || i.kind === "job") && i.cost ? Number(i.cost) : 0), 0);

  return (
    <div>
      {spend > 0 && (
        <div className="card-soft" style={{ padding: "14px 18px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="timeline-spend">
          <span className="lbl-m">Logged spend</span>
          <span className="font-serif-c" style={{ fontSize: 24 }}>€{spend.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
        {filters.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`timeline-filter-${f.key}`} style={{ cursor: "pointer", padding: "6px 14px", fontSize: 12, letterSpacing: ".04em", borderRadius: 999, border: `1px solid ${filter === f.key ? "var(--ink)" : "var(--line)"}`, background: filter === f.key ? "var(--ink)" : "var(--soft)", color: filter === f.key ? "var(--paper)" : "var(--ink)" }}>{f.label}</button>
        ))}
      </div>

      <div style={{ position: "relative" }} data-testid="timeline">
        {shown.map((it, idx) => {
          const meta = KIND[it.kind] || KIND.event;
          const Icon = meta.icon;
          const photos = (it.photos || []).map((p) => (typeof p === "string" ? p : p && p.url)).filter(Boolean);
          const last = idx === shown.length - 1;
          return (
            <div key={it.id || idx} style={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 14 }} data-testid={`timeline-item-${it.kind}`}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: meta.bg, color: meta.color, display: "grid", placeItems: "center", flexShrink: 0 }}><Icon size={17} /></div>
                {!last && <div style={{ width: 2, flex: 1, background: "var(--line)", minHeight: 18 }} />}
              </div>
              <div style={{ paddingBottom: 26, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span className="lbl-m" style={{ color: meta.color }}>{it.type}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{fmt(it.date)}</span>
                    {it.kind === "report" && <button onClick={() => setOpenId(it.id)} data-testid="timeline-open-report" className="btn-outline" style={{ padding: "4px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>Read report <ChevronRight size={14} /></button>}
                  </span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 500, margin: "3px 0 2px", wordBreak: "break-word" }}>{it.title}</div>
                {it.subtitle && <p style={{ color: "var(--muted)", fontSize: 14, margin: 0, wordBreak: "break-word" }}>{it.subtitle}</p>}
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                  {it.status && <StatusPill status={it.status} />}
                  {(it.kind === "event" || it.kind === "job") && it.cost != null && <span className="pill-tag" style={{ background: "#ece2f3", color: "#6b4e8a" }}>€{Number(it.cost).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>}
                  {it.author && <span style={{ fontSize: 12, color: "var(--muted)" }}>· {it.author}</span>}
                </div>
                {photos.length > 0 && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    {photos.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" onClick={() => setLb({ images: photos, index: i })} style={{ width: 92, height: 66, objectFit: "cover", borderRadius: 2, cursor: "pointer" }} />)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />}
      {openId && <ReportDetail reportId={openId} canManage={canManage} onClose={() => setOpenId(null)} onChanged={onChanged} />}
    </div>
  );
}
